import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { History, Search, Filter, RefreshCcw, User, Clock, FileText, Database, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/use-toast';
import { api } from '@/lib/api';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export default function HistoricoSystemPage() {
  const { toast } = useToast();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, per_page: 50, total: 0 });

  const fetchLogs = async (page = 1) => {
    setLoading(true);
    try {
      const response = await api.auditoria.list({ page });
      if (response && response.data) {
        setLogs(response.data);
        setPagination(response.pagination);
      }
    } catch (err) {
      toast({
        title: "Erro ao carregar histórico",
        description: "Não foi possível buscar os registros de auditoria.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  const getActionBadge = (action) => {
    switch (action?.toUpperCase()) {
      case 'INSERT': return <Badge className="bg-green-100 text-green-700 hover:bg-green-200 border-none px-2 py-0.5">INCLUSÃO</Badge>;
      case 'UPDATE': return <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-200 border-none px-2 py-0.5">ALTERAÇÃO</Badge>;
      case 'DELETE': return <Badge className="bg-red-100 text-red-700 hover:bg-red-200 border-none px-2 py-0.5">EXCLUSÃO</Badge>;
      case 'LOGIN': return <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-200 border-none px-2 py-0.5">LOGIN</Badge>;
      default: return <Badge variant="secondary" className="px-2 py-0.5">{action}</Badge>;
    }
  };

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-6">
      <Helmet>
        <title>Histórico e Auditoria - PROVATIO</title>
      </Helmet>

      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-blue-100/50 rounded-xl text-[#193D6D] shadow-sm border border-blue-100">
            <History size={28} />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-[#193D6D] tracking-tight">Histórico do Sistema</h1>
            <p className="text-slate-500 font-medium">Logs de auditoria e trilha de acessos centralizada.</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => fetchLogs()} disabled={loading} className="border-slate-300">
            <RefreshCcw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Sincronizar
          </Button>
          <Button variant="outline" className="border-slate-300">
            <Download className="w-4 h-4 mr-2" /> Exportar CSV
          </Button>
        </div>
      </div>

      <Card className="border-none shadow-xl bg-white/80 backdrop-blur-sm overflow-hidden">
        <CardHeader className="border-b border-slate-100 pb-6 bg-white">
          <div className="flex flex-col md:flex-row md:items-center gap-4 justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
              <Input placeholder="Pesquisar por usuário, tabela ou ação..." className="pl-10 bg-slate-50/50 border-slate-200 focus:bg-white transition-all shadow-inner" />
            </div>
            <Button variant="ghost" className="text-slate-500 hover:text-[#193D6D]">
              <Filter className="w-4 h-4 mr-2" /> Filtros por Data
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader className="bg-slate-50/50">
                <TableRow className="hover:bg-transparent border-slate-100">
                  <TableHead className="w-[180px] font-semibold text-slate-600">Data e Hora</TableHead>
                  <TableHead className="font-semibold text-slate-600">Usuário</TableHead>
                  <TableHead className="font-semibold text-slate-600">Ação / Tabela</TableHead>
                  <TableHead className="font-semibold text-slate-600">Registro</TableHead>
                  <TableHead className="text-right font-semibold text-slate-600">Detalhes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  Array.from({ length: 8 }).map((_, i) => (
                    <TableRow key={i} className="animate-pulse">
                      <TableCell colSpan={5} className="h-16 bg-slate-50/30"></TableCell>
                    </TableRow>
                  ))
                ) : logs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="h-48 text-center text-slate-400 italic font-medium">
                      Nenhum registro de auditoria encontrado.
                    </TableCell>
                  </TableRow>
                ) : (
                  logs.map((log) => (
                    <TableRow key={log.id} className="group hover:bg-slate-50 transition-all border-slate-50">
                      <TableCell className="font-medium text-slate-600">
                        <div className="flex items-center gap-2">
                          <Clock className="w-3.5 h-3.5 text-slate-400" />
                          <span className="text-xs">{format(new Date(log.created_at), "dd/MM/yyyy HH:mm", { locale: ptBR })}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="font-bold text-slate-700 flex items-center gap-1.5 text-sm">
                            <User className="w-3.5 h-3.5 text-blue-500" />
                            {log.usuario_nome || 'Sistema (Automático)'}
                          </span>
                          <span className="text-[10px] text-slate-400 font-mono tracking-tighter opacity-70">{log.usuario_email || 'n/a'}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          {getActionBadge(log.acao)}
                          <span className="flex items-center gap-1 text-slate-500 text-xs font-semibold uppercase tracking-wider">
                            <Database className="w-3.5 h-3.5 text-slate-400" />
                            {log.tabela.replace('_', ' ')}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-slate-500 bg-slate-50 font-mono text-[10px] border-slate-200">
                          ID: {log.registro_id}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <Button variant="ghost" size="sm" className="text-[#193D6D] hover:bg-[#193D6D] hover:text-white transition-all group-hover:px-4">
                          <FileText className="w-4 h-4 md:mr-1" />
                          <span className="hidden md:inline">Ver JSON</span>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {!loading && pagination.total > 0 && (
        <div className="flex flex-col md:flex-row items-center justify-between text-sm text-slate-500 bg-blue-50/30 p-4 rounded-xl shadow-sm border border-blue-100 gap-4">
          <div className="font-medium">
            Mostrando <span className="text-slate-900 font-bold">{logs.length}</span> de <span className="text-slate-900 font-bold">{pagination.total}</span> registros de auditoria.
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.page <= 1}
              onClick={() => fetchLogs(pagination.page - 1)}
              className="bg-white border-slate-200 text-[#193D6D] hover:bg-slate-50 h-9"
            >Anterior</Button>
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.page >= pagination.total_pages}
              onClick={() => fetchLogs(pagination.page + 1)}
              className="bg-white border-slate-200 text-[#193D6D] hover:bg-slate-50 h-9"
            >Próxima</Button>
          </div>
        </div>
      )}
    </div>
  );
}